import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { useUserRoleLevel } from "@/hooks/useUserRoleLevel";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";

/**
 * Props for the RoleProtectedRoute component.
 */
interface RoleProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Array of allowed role levels (2, 4, 5, 6, or 8)
   * User must have one of these levels to access the route
   */
  allowedLevels: number[];
}

/**
 * Route protection component that requires authentication and specific role levels.
 * 
 * Features:
 * - Checks if user is authenticated before checking role level
 * - Verifies user has one of the allowed role levels
 * - Subscribes to authentication state changes
 * - Shows loading state while checking authentication and role
 * - Redirects to /auth if user is not authenticated
 * - Redirects to home page with error message if user doesn't have required role level
 * 
 * This component wraps routes that require specific user role levels.
 * It ensures only users with the specified role levels can access protected pages.
 */
export const RoleProtectedRoute = ({ children, allowedLevels }: RoleProtectedRouteProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { roleLevel, loading: roleLoading } = useUserRoleLevel();

  /**
   * Effect: Sets up authentication state monitoring.
   * 
   * Process:
   * 1. Subscribes to auth state changes (sign in, sign out, token refresh)
   * 2. Checks for existing session immediately by reading localStorage directly
   * 3. Updates user state when auth state changes
   * 
   * Uses localStorage-based session check to bypass hanging Supabase client calls.
   */
  useEffect(() => {
    let isMounted = true;
    console.log('[RoleProtectedRoute] Setting up auth state monitoring');

    // Set up auth state listener FIRST to catch any changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[RoleProtectedRoute] Auth state changed:', event, 'User:', session?.user?.id || 'null');
        if (isMounted) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false); // CRITICAL: Set loading to false when auth state changes
        }
      }
    );

    // Check for existing session by reading localStorage directly (bypasses stuck Supabase client)
    console.log('[RoleProtectedRoute] Checking for existing session in localStorage...');
    
    const checkSession = async () => {
      try {
        // First, try to read session directly from localStorage
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const sessionKey = SUPABASE_URL ? `sb-${SUPABASE_URL.split('//')[1].split('.')[0]}-auth-token` : null;
        const storedSession = sessionKey ? localStorage.getItem(sessionKey) : null;
        
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession);
            if (sessionData.access_token && sessionData.user) {
              console.log('[RoleProtectedRoute] Found session in localStorage, setting user directly');
              if (isMounted) {
                setUser(sessionData.user);
                // Create a minimal session object
                setSession({
                  access_token: sessionData.access_token,
                  refresh_token: sessionData.refresh_token,
                  expires_at: sessionData.expires_at,
                  expires_in: sessionData.expires_in,
                  token_type: sessionData.token_type || 'bearer',
                  user: sessionData.user,
                } as Session);
                setLoading(false);
              }
              // Try to set session in Supabase client (non-blocking)
              supabase.auth.setSession({
                access_token: sessionData.access_token,
                refresh_token: sessionData.refresh_token,
              }).catch((err) => {
                console.warn('[RoleProtectedRoute] setSession failed (non-critical):', err);
              });
              return;
            }
          } catch (e) {
            console.warn('[RoleProtectedRoute] Error parsing stored session:', e);
          }
        }
        
        // Fallback: Try getSession with timeout (in case localStorage doesn't have it)
        console.log('[RoleProtectedRoute] No session in localStorage, trying getSession()...');
        const timeoutId = setTimeout(() => {
          if (isMounted) {
            console.warn('[RoleProtectedRoute] getSession() timeout after 2 seconds, continuing without session');
            setLoading(false);
          }
        }, 2000);
        
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise<{ data: { session: null }; error: null }>((resolve) => {
          setTimeout(() => {
            resolve({ data: { session: null }, error: null });
          }, 2000);
        });

        const result = await Promise.race([sessionPromise, timeoutPromise]);
        clearTimeout(timeoutId);
        
        console.log('[RoleProtectedRoute] getSession result - Session:', result.data?.session ? 'exists' : 'null', 'Error:', result.error?.message || 'none');
        if (!isMounted) {
          console.log('[RoleProtectedRoute] Component unmounted, ignoring getSession result');
          return;
        }
        
        if (result.error) {
          console.error('[RoleProtectedRoute] Error getting session:', result.error);
          
          // Capture session error to Sentry
          Sentry.captureException(result.error, {
            tags: {
              operation: 'session-check',
              errorType: 'authentication',
            },
            extra: {
              errorCode: (result.error as any).status || 'unknown',
              errorMessage: result.error.message,
              timestamp: new Date().toISOString(),
            },
          });
          
          setSession(null);
          setUser(null);
        } else {
          setSession(result.data?.session ?? null);
          setUser(result.data?.session?.user ?? null);
        }
        setLoading(false);
      } catch (error) {
        console.error('[RoleProtectedRoute] Unexpected error getting session:', error);
        
        // Capture unexpected error to Sentry
        Sentry.captureException(error, {
          tags: {
            operation: 'session-check',
            errorType: 'unexpected',
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        });
        
        if (isMounted) {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
      }
    };
    
    checkSession();

    return () => {
      console.log('[RoleProtectedRoute] Cleaning up auth state monitoring');
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Effect: Check role level after authentication is confirmed
   * Redirects to home page if user doesn't have required role level
   */
  useEffect(() => {
    // Wait for both auth and role loading to complete
    if (loading || roleLoading) {
      return;
    }

    // If not authenticated, ProtectedRoute will handle redirect
    if (!user) {
      return;
    }

    // Check if user has one of the allowed role levels
    if (!allowedLevels.includes(roleLevel)) {
      console.log('[RoleProtectedRoute] User does not have required role level. User level:', roleLevel, 'Required levels:', allowedLevels);
      toast.error("You don't have permission to access this page");
      navigate('/', { replace: true });
    }
  }, [user, roleLevel, loading, roleLoading, allowedLevels, navigate]);

  console.log('[RoleProtectedRoute] Render - Loading:', loading || roleLoading, 'User:', user?.id || 'null', 'Role Level:', roleLevel, 'Allowed Levels:', allowedLevels);

  // Show loading state while checking authentication or role
  if (loading || roleLoading) {
    console.log('[RoleProtectedRoute] Rendering loading state');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Redirect to auth if not authenticated
  if (!user) {
    console.log('[RoleProtectedRoute] No user, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  // Check if user has required role level
  if (!allowedLevels.includes(roleLevel)) {
    console.log('[RoleProtectedRoute] User does not have required role level, redirecting to home');
    // The useEffect above will handle the redirect and toast, but we need to return something here
    // Return null to prevent rendering children while redirecting
    return null;
  }

  console.log('[RoleProtectedRoute] User authenticated and has required role level, rendering children');
  return <>{children}</>;
};









